<template>
    <v-container :id="story.content._uid"
        class="space-y-2xl pb-xl">
        <div class="flex flex-col space-y-md">
            <v-h3 class="mx-sm sm:mx-0">
                {{ story.content.title }}
            </v-h3>

            <div v-if="story.content.content"
                class="mx-sm text-gray-500 sm:mx-0">
                <sb-rich-text :document="story.content.content" />
            </div>

            <nuxt-picture v-if="story.content.image?.filename"
                :src="story.content.image.filename"
                :alt="story.content.title"
                provider="storyblok"
                loading="lazy"
                sizes="xs:100vw sm:640px md:768px lg:1024px xl:1280px 2xl:1536px"
                :img-attrs="{
                    class: 'object-cover lg:rounded aspect-[3.57/1]'
                }" />
        </div>

        <v-scrollable v-if="subCategories.length > 0"
            class="flex snap-x scroll-px-sm flex-nowrap space-x-5 px-sm sm:px-0 md:scroll-px-0">
            <v-scrollable-item v-for="category in subCategories"
                :key="category.id"
                class="w-44 shrink-0 snap-start">
                <nuxt-link :to="formatSbUrl(category.full_slug)"
                    :aria-label="category.name"

                    class="flex flex-col space-y-xs hover:underline">
                    <nuxt-picture
                        v-if="category.content.image?.filename && category.content.image?.filename.length > 0"
                        :src="category.content.image.filename"
                        :alt="story.content.title"
                        provider="storyblok"
                        loading="lazy"
                        height="100"
                        width="176"
                        sizes="xs:176px"
                        class="w-full"
                        :img-attrs="{
                            class: 'object-cover rounded aspect-[1.76/1] w-full h-full',
                        }" />

                    <span v-else
                        class="aspect-[1.76/1] h-[100px] rounded bg-gray-50">
                    </span>

                    <span>
                        {{ category.name }}
                    </span>
                </nuxt-link>
            </v-scrollable-item>
        </v-scrollable>

        <v-elevate-product-list
            v-slot="{
                products,
                productCount,
                hasNextPage,
                loadMore,
                activeFiltersCount,
                clearFilter
            }"
            :campaign-only="isCampaignOnly"
            class="space-y-lg px-sm sm:px-0">
            <div class="flex flex-col justify-between">
                <p class="text-sm tracking-xl text-gray-500 md:block">
                    {{ productCount }} {{ t('amount-products') }}
                </p>

                <div class="mt-sm flex gap-lg lg:pt-md">
                    <v-button type="button"
                        theme="black"
                        size="sm"
                        class="flex items-center space-x-xs hover:underline"
                        @click="isModalOpen = true">
                        <template v-if="activeFiltersCount > 0">
                            <v-category-filter-count :active-filters-count="activeFiltersCount" />
                        </template>

                        <span>{{ t('filter-title') }}</span>

                        <v-icon icon="filter"
                            class="text-lg"></v-icon>
                    </v-button>

                    <v-button v-if="activeFiltersCount > 0"
                        theme="gray"
                        size="sm"
                        @click="clearFilter">
                        {{ t('filter-clear') }}
                    </v-button>
                </div>

                <v-elevate-common-facets class="mt-xs" />

                <v-category-order class="mt-md" />
            </div>

            <div class="grid grid-cols-2 gap-x-xs gap-y-lg
                md:grid-cols-3 md:gap-x-sm md:gap-y-xl lg:grid-cols-4">
                <template v-for="(product, index) in products"
                    :key="product.key">
                    <v-category-cta v-if="(index > 0 && index % 12 === 0) && ctaContent != null && isCtaVisible"
                        :content="ctaContent"
                        class="col-span-full" />

                    <v-elevate-product-card :product="product" />
                </template>
            </div>

            <div class="grid md:grid-cols-8">
                <div class="space-y-md md:col-span-2 md:col-start-4">
                    <v-button class="w-full"
                        :theme="hasNextPage ? 'black' : 'gray'"
                        :disabled="!hasNextPage"
                        :aria-label="hasNextPage ? $t('loader.show-more') : $t('loader.show-more-loaded')"
                        @click="loadMore">
                        {{ hasNextPage ? $t('loader.show-more') : $t('loader.show-more-loaded') }}
                    </v-button>

                    <div class="space-y-xs">
                        <div class="h-[2px] rounded-full bg-gray-300">
                            <div class="h-[2px] rounded-full bg-black"
                                :style="{
                                    width: `${(products.length / productCount) * 100}%`
                                }"></div>
                        </div>

                        <p class="text-center">
                            {{ $t('loader.show-more-prefix') }} {{ products.length }}
                            / {{ productCount }} {{ $t('loader.show-more-suffix') }}
                        </p>
                    </div>

                    <button class="w-full text-base underline"
                        type="button"
                        @click="scrollToTop">
                        <span class="">{{ $t('loader.back-to-start') }}</span>
                    </button>
                </div>
            </div>

            <v-category-filter-modal :is-open="isModalOpen"
                :results="productCount"
                @close="isModalOpen = false" />
        </v-elevate-product-list>
    </v-container>
</template>

<script setup lang="ts">
import type { ISbStoryData } from 'storyblok-js-client';
import type { ISbRichtext } from '@storyblok/js';
import type {
    BlokAsset,
    MetaFieldsPlugin,
    CmdProductListPlugin,
} from '~~/types';

const properties = defineProps<{
    story: ISbStoryData<SbDiscounts>
}>();

const { t } = useI18n();
const { format: formatSbUrl } = useSbUrl();

interface SbDiscounts {
    component: 'sb-discounts'
    meta: MetaFieldsPlugin
    image: BlokAsset
    title: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    short_description: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    category_menu: string[]
    content: ISbRichtext
    products: CmdProductListPlugin
    // eslint-disable-next-line @typescript-eslint/naming-convention
    campaign_only: boolean
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _uid: string
}

const isModalOpen = ref(false);
const isCampaignOnly = ref(true);

const ctaContent = useStoryblokStory('global/category-cta');

const categoryId = computed(() => properties.story.uuid);
const visibleOnPage = computed(() => ctaContent.value?.content.on_pages);

const isCtaVisible = computed(() => (
    visibleOnPage.value?.length && visibleOnPage.value.includes(categoryId.value)
));

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const subCategories = await useAsyncStoryblokStories(`subcategories-${properties.story.full_slug}`, {
    /* eslint-disable @typescript-eslint/naming-convention */
    starts_with: properties.story.full_slug,
    excluding_ids: `${properties.story.id}`,
    /* eslint-disable @typescript-eslint/naming-convention */
});

useDefaultDynamicHead({ includeQueryInCanonicalUrl: false });
</script>

<i18n lang="json">
{
    "et": {
        "amount-products": "toodet",
        "filter-title": "Filtrid"
    },
    "fi": {
        "amount-products": "tuotetta",
        "filter-title": "Suodata"
    }
}
</i18n>
